import * as Select from "@radix-ui/react-select";
import PiChevronDownStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronDownStroke";
import PiCheckTickSingleStroke from "@markeroo/icons/src/stroke/general/PiCheckTickSingleStroke";
import { CRITERIA_CATEGORY_COLORS, type CriteriaCategory } from "@markeroo/theme";
import { ui } from "@/strings";

interface CategoryOption {
  value: CriteriaCategory;
  label: string;
  description: string;
  color: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  {
    value: "claims",
    label: ui.categories.claims,
    description: ui.categories.claimsDescription,
    color: CRITERIA_CATEGORY_COLORS.claims,
  },
  {
    value: "support",
    label: ui.categories.support,
    description: ui.categories.supportDescription,
    color: CRITERIA_CATEGORY_COLORS.support,
  },
  {
    value: "reasoning",
    label: ui.categories.reasoning,
    description: ui.categories.reasoningDescription,
    color: CRITERIA_CATEGORY_COLORS.reasoning,
  },
  {
    value: "flow",
    label: ui.categories.flow,
    description: ui.categories.flowDescription,
    color: CRITERIA_CATEGORY_COLORS.flow,
  },
  {
    value: "style",
    label: ui.categories.style,
    description: ui.categories.styleDescription,
    color: CRITERIA_CATEGORY_COLORS.style,
  },
  {
    value: "mechanics",
    label: ui.categories.mechanics,
    description: ui.categories.mechanicsDescription,
    color: CRITERIA_CATEGORY_COLORS.mechanics,
  },
  {
    value: "other",
    label: ui.categories.other,
    description: ui.categories.otherDescription,
    color: CRITERIA_CATEGORY_COLORS.other,
  },
];

interface CategorySelectorProps {
  selectedCategory: CriteriaCategory;
  onCategoryChange: (category: CriteriaCategory) => void;
}

/**
 * CategorySelector - A dropdown selector for criteria categories.
 *
 * Uses Radix UI Select to display a compact trigger that expands into
 * a dropdown showing each category with its color swatch and description.
 */
export function CategorySelector({
  selectedCategory,
  onCategoryChange,
}: CategorySelectorProps) {
  const selectedOption = CATEGORY_OPTIONS.find(
    (opt) => opt.value === selectedCategory
  );

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-theme-secondary">
        {ui.categories.label}
      </label>

      <Select.Root
        value={selectedCategory}
        onValueChange={(value) => onCategoryChange(value as CriteriaCategory)}
      >
        {/* Trigger - looks like a select input */}
        <Select.Trigger className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border bg-white hover:border-border-hover focus:outline-none focus:ring-2 focus:ring-primary/40 transition">
          {selectedOption && (
            <>
              <div
                className="h-4 w-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: selectedOption.color }}
                aria-hidden="true"
              />
              <span className="flex-1 text-left text-sm text-text-theme-primary">
                {selectedOption.label}
              </span>
            </>
          )}
          <Select.Icon>
            <PiChevronDownStroke
              size={20}
              className="text-text-theme-muted flex-shrink-0"
            />
          </Select.Icon>
        </Select.Trigger>

        {/* Dropdown content */}
        <Select.Portal>
          <Select.Content
            className="bg-white rounded-xl shadow-lg border border-border overflow-hidden z-[9999]"
            position="popper"
            sideOffset={4}
            style={{ minWidth: "var(--radix-select-trigger-width)" }}
          >
            <Select.Viewport className="p-2">
              {CATEGORY_OPTIONS.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-md cursor-pointer outline-none hover:bg-brand-50 data-[highlighted]:bg-brand-50 data-[state=checked]:bg-brand-100 transition-colors"
                >
                  <div
                    className="h-4 w-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: option.color }}
                    aria-hidden="true"
                  />
                  <Select.ItemText>
                    <span className="text-sm font-medium text-text-theme-primary">
                      {option.label}
                    </span>
                  </Select.ItemText>
                  <span className="text-sm text-text-theme-muted">
                    {option.description}
                  </span>
                  <Select.ItemIndicator className="ml-auto flex-shrink-0">
                    <PiCheckTickSingleStroke size={16} className="text-primary" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}

/**
 * Re-export color utilities for components that need color access.
 */
export { CRITERIA_CATEGORY_COLORS, type CriteriaCategory };
